import { t } from "@/i18n-js/instance";
import { PRICE_SCENARIOS } from "@circle-react/helpers/paywallPriceHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { PaywallCheckoutPreview } from "@circle-react/types";
import { CheckoutFormSection } from "../../../CheckoutFormSection";
import { PreviewEntry } from "../components/PreviewEntry";
import { DEFAULT_FULL_PREVIEW_VARIANT } from "../constants";

interface PaymentAfterTrialPreviewProps {
  className?: string;
  preview: PaywallCheckoutPreview;
  variant?: keyof typeof DEFAULT_FULL_PREVIEW_VARIANT;
}

export const PaymentAfterTrialPreview = ({
  preview,
  className,
  variant,
}: PaymentAfterTrialPreviewProps) => {
  if (
    variant === DEFAULT_FULL_PREVIEW_VARIANT.default ||
    ![PRICE_SCENARIOS.trialThenUpfrontThenValue].includes(
      preview.preview_scenario,
    ) ||
    !preview.trial_period
  ) {
    return null;
  }

  const hasDiscount = preview.first_payment.discount < 0;
  const hasTax = preview.first_payment.tax > 0;
  const hasTaxOrDiscount = hasTax || hasDiscount;

  return (
    <CheckoutFormSection className={classNames("gap-y-2 border-t", className)}>
      <PreviewEntry
        label={t(
          "paywall_checkout.payment_after_trial.intro_fee_after_x_trial_days",
          { count: preview.trial_period.days },
        )}
        labelWeight="semibold"
        value={
          hasTaxOrDiscount
            ? preview.first_payment.formatted_amount_due
            : undefined
        }
        valueWeight={hasTaxOrDiscount ? "semibold" : "normal"}
      />
      {hasTaxOrDiscount && (
        <PreviewEntry
          label={t("subtotal")}
          value={preview.first_payment.formatted_subtotal}
        />
      )}
      {hasDiscount && (
        <PreviewEntry
          label={t("paywall_checkout.coupon.discount")}
          valueColor="text-green-600"
          value={preview.first_payment.formatted_discount}
        />
      )}
      {hasTax && (
        <PreviewEntry
          label={t("paywall_checkout.coupon.tax")}
          value={preview.first_payment.formatted_tax}
        />
      )}
      <PreviewEntry
        label={t(
          "paywall_checkout.payment_after_trial.onetime_payment_due_on",
          { date: preview.first_payment.formatted_due_on },
        )}
        value={preview.first_payment.formatted_amount_due}
        valueWeight={hasTaxOrDiscount ? "normal" : "semibold"}
      />
    </CheckoutFormSection>
  );
};
